import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, ExternalLink, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorState, LoadingState, Notice, PageHeader, Spinner, StatusBadge } from "@/components/common/States";
import { useAuth, useSchoolCan, useSpaceName } from "@/context/AuthContext";
import { api, errorMessage, fetcher } from "@/lib/api";
import { TENANT_STATUS } from "@/lib/labels";
import { ReadOnlyBanner } from "./SchoolHome";

const FIELDS = [
  ["principal_name", "Nama kepala sekolah"], ["email", "Email sekolah"], ["phone", "Telepon"],
  ["website", "Situs web"], ["address", "Alamat"], ["city", "Kabupaten/Kota"], ["province", "Provinsi"],
];

const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

function ProfilTab() {
  const canEdit = useSchoolCan("profile:update");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["school-profile"], queryFn: () => fetcher("/school/profile") });
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (q.data) setF(Object.fromEntries(FIELDS.map(([k]) => [k, q.data[k] || ""])));
  }, [q.data]);

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const s = q.data;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const changes = Object.fromEntries(Object.entries(f).filter(([k, v]) => (s[k] || "") !== v).map(([k, v]) => [k, v || null]));
      if (!Object.keys(changes).length) return setErr("Tidak ada perubahan untuk disimpan.");
      await api.patch("/school/profile", changes);
      toast.success("Profil sekolah disimpan.");
      qc.invalidateQueries();
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border bg-card p-6 grid sm:grid-cols-3 gap-4" data-testid="profile-fixed-info">
        <div><p className="text-xs text-muted-foreground">ID sekolah</p><p className="font-mono text-sm">{s.id}</p></div>
        <div><p className="text-xs text-muted-foreground">NPSN · Jenjang</p><p className="font-medium">{s.npsn || "—"} · {s.level}</p></div>
        <div><p className="text-xs text-muted-foreground">Status</p><StatusBadge map={TENANT_STATUS} value={s.status} /></div>
        <p className="sm:col-span-3 text-xs text-muted-foreground">Nama, NPSN, dan status sekolah dikelola oleh Admin Platform.</p>
      </div>
      <form onSubmit={save} className="grid sm:grid-cols-2 gap-5" noValidate data-testid="profile-form">
        {FIELDS.map(([k, label]) => (
          <div key={k} className={`space-y-2 ${k === "address" ? "sm:col-span-2" : ""}`}>
            <Label htmlFor={k}>{label}</Label>
            <Input id={k} value={f[k] ?? ""} disabled={!canEdit} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`profile-${k}-input`} />
          </div>
        ))}
        <div className="sm:col-span-2 space-y-3">
          {err && <Notice tone="danger" testId="profile-error">{err}</Notice>}
          {canEdit && <Button type="submit" disabled={busy} data-testid="profile-save-button">{busy && <Spinner />} Simpan profil</Button>}
        </div>
      </form>
    </div>
  );
}

function HalamanPublikTab() {
  const { me } = useAuth();
  const canSettings = useSchoolCan("feed:settings");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["feed-settings"], queryFn: () => fetcher("/school/feed/settings") });
  const simpan = useMutation({
    mutationFn: (body) => api.put("/school/feed/settings", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feed-settings"] });
      qc.invalidateQueries({ queryKey: ["feed-context"] });
    },
  });
  if (q.isLoading) return <LoadingState rows={2} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const aktif = q.data.public_page_enabled;
  const url = `${window.location.origin}/publik/${me.active_school.school_id}`;
  const salin = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Alamat halaman publik disalin.");
  };
  return (
    <div className="space-y-5 max-w-2xl">
      <div className="rounded-2xl border bg-card p-6 space-y-4" data-testid="public-settings-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold">Halaman publik sekolah</p>
            <p className="text-sm text-muted-foreground">
              Bila aktif, pengumuman yang penulisnya tandai "tampilkan di halaman publik" dapat dibaca siapa saja tanpa masuk, tanpa nama penulis dan tanpa komentar.
            </p>
          </div>
          <Switch checked={aktif} disabled={!canSettings || simpan.isPending} data-testid="public-page-switch"
            onCheckedChange={(v) => simpan.mutate({ public_page_enabled: v, allowed_domains: q.data.allowed_domains }, {
              onSuccess: () => toast.success(v ? "Halaman publik diaktifkan." : "Halaman publik dinonaktifkan. Tanda publik pada postingan ikut dibersihkan."),
            })} />
        </div>
        {!canSettings && <Notice tone="muted">Hanya Admin Sekolah yang dapat mengubah pengaturan feed.</Notice>}
        <div className="space-y-2">
          <Label htmlFor="public-url">Alamat halaman publik</Label>
          <div className="flex flex-wrap gap-2">
            <Input id="public-url" readOnly value={url} className="font-mono text-xs" data-testid="public-url-input" />
            <Button type="button" variant="outline" size="sm" onClick={salin} data-testid="public-url-copy"><Copy className="mr-1.5 h-4 w-4" /> Salin</Button>
            <Button type="button" variant="outline" size="sm" asChild data-testid="public-url-open">
              <a href={url} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-1.5 h-4 w-4" /> Buka</a>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Bagikan alamat ini ke warga sekolah atau masyarakat umum bila halaman sudah diaktifkan.</p>
        </div>
      </div>
      <Notice tone="info">
        Postingan hanya tampil di halaman publik bila sasaran "seluruh sekolah" dan penulis menandai "Tampilkan di halaman publik" saat menerbitkan atau lewat menu postingan.
      </Notice>
    </div>
  );
}

function PengaturanFeedTab() {
  const canSettings = useSchoolCan("feed:settings");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["feed-settings"], queryFn: () => fetcher("/school/feed/settings") });
  const [domain, setDomain] = useState("");
  const [daftar, setDaftar] = useState(null);
  const simpan = useMutation({
    mutationFn: (body) => api.put("/school/feed/settings", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feed-settings"] });
      qc.invalidateQueries({ queryKey: ["feed-context"] });
      toast.success("Pengaturan feed disimpan.");
    },
  });
  useEffect(() => {
    if (q.data) setDaftar(q.data.allowed_domains);
  }, [q.data]);
  if (q.isLoading) return <LoadingState rows={2} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const aktif = q.data.public_page_enabled;
  const tambah = () => {
    const d = domain.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/.*$/, "");
    if (!DOMAIN_RE.test(d)) return toast.error("Domain tidak valid. Contoh: youtube.com");
    if (daftar.includes(d)) return toast.error("Domain sudah ada dalam daftar.");
    setDaftar([...daftar, d]);
    setDomain("");
  };
  return (
    <div className="space-y-5 max-w-2xl">
      <div className="rounded-2xl border bg-card p-6 space-y-4" data-testid="feed-domains-card">
        <div>
          <p className="font-semibold">Domain tautan yang diizinkan</p>
          <p className="text-sm text-muted-foreground">
            Pratinjau tautan hanya diambil dari domain ini. Tautan di luar daftar tetap bisa dibagikan, tetapi ditampilkan sebagai kartu polos tanpa judul dan gambar.
          </p>
        </div>
        <ul className="flex flex-wrap gap-2" data-testid="feed-domain-list">
          {(daftar || []).map((d) => (
            <li key={d} className="inline-flex items-center gap-1.5 rounded-full border bg-muted px-3 py-1 text-xs font-semibold">
              {d}
              {canSettings && (
                <button type="button" onClick={() => setDaftar(daftar.filter((x) => x !== d))}
                  aria-label={`Hapus domain ${d}`} className="text-muted-foreground hover:text-[#E11D2E]" data-testid={`feed-domain-remove-${d}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
        {canSettings && (
          <div className="flex flex-wrap gap-2">
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="contoh: youtube.com"
              className="max-w-xs" aria-label="Domain baru" data-testid="feed-domain-input" />
            <Button type="button" variant="outline" onClick={tambah} data-testid="feed-domain-add"><Plus className="mr-1.5 h-4 w-4" /> Tambah</Button>
            <Button type="button" disabled={simpan.isPending} data-testid="feed-domains-save"
              onClick={() => simpan.mutate({ public_page_enabled: aktif, allowed_domains: daftar })}>
              {simpan.isPending && <Spinner />} Simpan pengaturan
            </Button>
          </div>
        )}
        {!canSettings && <Notice tone="muted">Hanya Admin Sekolah yang dapat mengubah pengaturan feed.</Notice>}
      </div>
    </div>
  );
}

export default function SchoolProfile() {
  const space = useSpaceName();
  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader eyebrow={space} title="Profil Sekolah" />
      <ReadOnlyBanner />
      <Tabs defaultValue="profil">
        <TabsList data-testid="profile-tabs">
          <TabsTrigger value="profil" data-testid="profile-tab-profil">Profil</TabsTrigger>
          <TabsTrigger value="publik" data-testid="profile-tab-publik">Halaman Publik</TabsTrigger>
          <TabsTrigger value="pengaturan" data-testid="profile-tab-pengaturan">Pengaturan Feed</TabsTrigger>
        </TabsList>
        <TabsContent value="profil" className="mt-6"><ProfilTab /></TabsContent>
        <TabsContent value="publik" className="mt-6"><HalamanPublikTab /></TabsContent>
        <TabsContent value="pengaturan" className="mt-6"><PengaturanFeedTab /></TabsContent>
      </Tabs>
    </div>
  );
}
